
import React, { useEffect, useState } from "react";
import API_URL from "../../services/api";
import "./Staff.css";

const initialForm = {
  staff_id: "",
  first_name: "",
  last_name: "",
  position: "",
  cnic: "",
  phone: "",
  email: "",
  address: "",
  joining_date: "",
  status: "Active",
};

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(initialForm);

  // ==============================
  // Fetch Staff
  // ==============================

  const fetchStaff = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/staff`);

      if (!res.ok) {
        throw new Error("Failed to fetch staff");
      }

      const data = await res.json();
      setStaff(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Staff fetch error:", err);
      alert("Unable to load staff records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // ==============================
  // Handle Input
  // ==============================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ==============================
  // Add / Update Staff
  // ==============================

  const saveStaff = async () => {
    const requiredFields = [
      "staff_id",
      "first_name",
      "last_name",
      "position",
      "cnic",
      "phone",
      "email",
      "address",
      "joining_date",
    ];

    const missingField = requiredFields.find(
      (field) => !form[field]?.trim()
    );

    if (missingField) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      setSaving(true);

      const url = editingId
        ? `${API_URL}/api/staff/${editingId}`
        : `${API_URL}/api/staff`;

      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Unable to save staff");
      }

      alert(
        data.message ||
          (editingId
            ? "Staff updated successfully."
            : "Staff added successfully.")
      );

      setForm(initialForm);
      setEditingId(null);

      await fetchStaff();
    } catch (err) {
      console.error("Save staff error:", err);
      alert(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  // ==============================
  // Edit Staff
  // ==============================

  const editStaff = (item) => {
    setEditingId(item.id);

    setForm({
      staff_id: item.staff_id || "",
      first_name: item.first_name || "",
      last_name: item.last_name || "",
      position: item.position || "",
      cnic: item.cnic || "",
      phone: item.phone || "",
      email: item.email || "",
      address: item.address || "",
      joining_date: item.joining_date
        ? item.joining_date.split("T")[0]
        : "",
      status: item.status || "Active",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ==============================
  // Cancel Edit
  // ==============================

  const cancelEdit = () => {
    setEditingId(null);
    setForm(initialForm);
  };

  // ==============================
  // Delete Staff
  // ==============================

  const deleteStaff = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this staff member?"
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/staff/${id}`, {
        method: "DELETE",
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Unable to delete staff");
      }

      alert(data.message || "Staff deleted successfully.");

      await fetchStaff();
    } catch (err) {
      console.error("Delete staff error:", err);
      alert(err.message || "Unable to delete staff.");
    }
  };

  // ==============================
  // Search
  // ==============================

  const filteredStaff = staff.filter((item) => {
    const searchText = search.toLowerCase();

    return (
      String(item.staff_id || "")
        .toLowerCase()
        .includes(searchText) ||
      String(item.first_name || "")
        .toLowerCase()
        .includes(searchText) ||
      String(item.last_name || "")
        .toLowerCase()
        .includes(searchText) ||
      String(item.position || "")
        .toLowerCase()
        .includes(searchText) ||
      String(item.cnic || "")
        .toLowerCase()
        .includes(searchText) ||
      String(item.phone || "")
        .toLowerCase()
        .includes(searchText) ||
      String(item.email || "")
        .toLowerCase()
        .includes(searchText)
    );
  });

  // ==============================
  // Statistics
  // ==============================

  const totalStaff = staff.length;

  const active = staff.filter(
    (item) => item.status === "Active"
  ).length;

  const inactive = staff.filter(
    (item) => item.status === "Inactive"
  ).length;

  const leave = staff.filter(
    (item) => item.status === "On Leave"
  ).length;

  // ==============================
  // Download CSV
  // ==============================

  const downloadCSV = () => {
    if (filteredStaff.length === 0) {
      alert("No staff data available.");
      return;
    }

    const headers = [
      "Staff ID",
      "First Name",
      "Last Name",
      "Position",
      "CNIC",
      "Phone",
      "Email",
      "Address",
      "Joining Date",
      "Status",
    ];

    const rows = filteredStaff.map((item) => [
      item.staff_id,
      item.first_name,
      item.last_name,
      item.position,
      item.cnic,
      item.phone,
      item.email,
      item.address,
      item.joining_date
        ? new Date(item.joining_date).toLocaleDateString()
        : "",
      item.status,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) => `"${String(field ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Staff_Details_${
      new Date().toISOString().split("T")[0]
    }.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  // ==============================
  // Render
  // ==============================

  return (
    <div className="staff-page">

      {/* ==============================
          HEADER
      ============================== */}

      <div className="staff-header">
        <div>
          <h1>Staff Management</h1>
          <p>Manage library staff records and information.</p>
        </div>

        {editingId && (
          <button
            className="cancel-edit-btn"
            onClick={cancelEdit}
          >
            Cancel Edit
          </button>
        )}
      </div>

      {/* ==============================
          STATISTICS
      ============================== */}

      <div className="staff-stats">

        <div className="staff-stat-card">
          <div className="stat-icon">👥</div>
          <div>
            <h2>{totalStaff}</h2>
            <p>Total Staff</p>
          </div>
        </div>

        <div className="staff-stat-card active">
          <div className="stat-icon">✅</div>
          <div>
            <h2>{active}</h2>
            <p>Active</p>
          </div>
        </div>

        <div className="staff-stat-card inactive">
          <div className="stat-icon">❌</div>
          <div>
            <h2>{inactive}</h2>
            <p>Inactive</p>
          </div>
        </div>

        <div className="staff-stat-card leave">
          <div className="stat-icon">⏳</div>
          <div>
            <h2>{leave}</h2>
            <p>On Leave</p>
          </div>
        </div>

      </div>

      {/* ==============================
          STAFF FORM
      ============================== */}

      <div className="staff-form-card">

        <div className="section-title">
          <h2>
            {editingId
              ? "Edit Staff Member"
              : "Add New Staff Member"}
          </h2>

          <p>
            Enter the staff member's information below.
          </p>
        </div>

        <div className="staff-form">

          <div className="form-group">
            <label>Staff ID *</label>
            <input
              type="text"
              name="staff_id"
              value={form.staff_id}
              onChange={handleChange}
              placeholder="e.g. STF-001"
            />
          </div>

          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              placeholder="First name"
            />
          </div>

          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              placeholder="Last name"
            />
          </div>

          <div className="form-group">
            <label>Position *</label>
            <select
              name="position"
              value={form.position}
              onChange={handleChange}
            >
              <option value="">Select Position</option>
              <option value="President">President</option>
              <option value="General Secretary">
                General Secretary
              </option>
              <option value="Finance Secretary">
                Finance Secretary
              </option>
              <option value="Manager">Manager</option>
              <option value="Librarian">Librarian</option>
              <option value="Gate Keeper">Gate Keeper</option>
              <option value="Helper">Helper</option>
            </select>
          </div>

          <div className="form-group">
            <label>CNIC *</label>
            <input
              type="text"
              name="cnic"
              value={form.cnic}
              onChange={handleChange}
              placeholder="XXXXX-XXXXXXX-X"
            />
          </div>

          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="03XX-XXXXXXX"
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="staff@example.com"
            />
          </div>

          <div className="form-group">
            <label>Joining Date *</label>
            <input
              type="date"
              name="joining_date"
              value={form.joining_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>

          <div className="form-group address-field">
            <label>Home Address *</label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Enter complete home address"
            />
          </div>

        </div>

        <div className="form-actions">

          <button
            className="save-staff-btn"
            onClick={saveStaff}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : editingId
              ? "Update Staff"
              : "Add Staff"}
          </button>

          {editingId && (
            <button
              className="cancel-btn"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          )}

        </div>

      </div>

      {/* ==============================
          SEARCH + CSV
      ============================== */}

      <div className="staff-toolbar">

        <input
          type="text"
          className="staff-search"
          placeholder="Search Staff ID, Name, CNIC, Position, Phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          className="download-btn"
          onClick={downloadCSV}
        >
          📥 Download CSV
        </button>

      </div>

      {/* ==============================
          TABLE
      ============================== */}

      <div className="staff-table-card">

        {loading ? (
          <div className="staff-loading">
            <div className="loading-spinner"></div>
            <p>Loading staff records...</p>
          </div>
        ) : (
          <div className="staff-table-wrapper">

            <table className="staff-table">

              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>CNIC</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Joining Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>

                {filteredStaff.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="no-staff"
                    >
                      No staff records found.
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map((item) => (

                    <tr key={item.id}>

                      <td data-label="Staff ID">
                        {item.staff_id}
                      </td>

                      <td data-label="Name">
                        <strong>
                          {item.first_name}{" "}
                          {item.last_name}
                        </strong>
                      </td>

                      <td data-label="Position">
                        {item.position}
                      </td>

                      <td data-label="CNIC">
                        {item.cnic}
                      </td>

                      <td data-label="Phone">
                        {item.phone}
                      </td>

                      <td data-label="Email">
                        {item.email}
                      </td>

                      <td data-label="Joining Date">
                        {item.joining_date
                          ? new Date(
                              item.joining_date
                            ).toLocaleDateString()
                          : "-"}
                      </td>

                      <td data-label="Status">

                        <span
                          className={`status-badge ${
                            item.status === "Active"
                              ? "status-active"
                              : item.status === "Inactive"
                              ? "status-inactive"
                              : "status-leave"
                          }`}
                        >
                          {item.status}
                        </span>

                      </td>

                      <td
                        data-label="Actions"
                        className="action-cell"
                      >

                        <button
                          className="edit-btn"
                          onClick={() =>
                            editStaff(item)
                          }
                        >
                          ✏️ Edit
                        </button>

                        <button
                          className="delete-btn"
                          onClick={() =>
                            deleteStaff(item.id)
                          }
                        >
                          🗑️ Delete
                        </button>

                      </td>

                    </tr>

                  ))
                )}

              </tbody>

            </table>

          </div>
        )}

      </div>

    </div>
  );
};

export default Staff;

